import { useState } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { useSettings } from "@/lib/settings";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const telHref = `tel:${settings.phone.replace(/[^0-9+]/g, "")}`;
  const info = [
    { icon: Phone, label: settings.phone, href: telHref, testid: "contact-phone-link" },
    { icon: Mail, label: settings.email, href: `mailto:${settings.email}`, testid: "contact-email-link" },
    { icon: MapPin, label: settings.location, href: `https://maps.google.com/?q=${encodeURIComponent(settings.location)}`, external: true, testid: "contact-map-link" },
    { icon: Clock, label: settings.hours },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="GET IN TOUCH" title="LET'S TALK" script="we'd love to hear from you" />
      <section className="py-20 bg-paper">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <h2 className="font-display text-3xl mb-6">CONTACT INFORMATION</h2>
            <p className="text-navy/65 leading-relaxed mb-8 max-w-md">Questions about a project or just want to say hello? Reach out any time — our family-run shop responds fast.</p>
            <ul className="space-y-5">
              {info.map((it) => (
                <li key={it.label} className="flex items-center gap-4">
                  <span className="w-11 h-11 bg-ink text-gold flex items-center justify-center shrink-0"><it.icon size={18} /></span>
                  {it.href ? (
                    <a
                      href={it.href}
                      data-testid={it.testid}
                      {...(it.external ? { target: "_blank", rel: "noreferrer" } : {})}
                      className="text-navy hover:text-gold transition-colors"
                    >{it.label}</a>
                  ) : (
                    <span className="text-navy">{it.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.12}>
            <form onSubmit={submit} className="bg-white p-8 shadow-sm border border-line" data-testid="contact-form">
              <div className="space-y-5">
                <input required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" className="w-full border border-line px-4 py-3 outline-none focus:border-navy transition-colors" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" data-testid="contact-email" className="w-full border border-line px-4 py-3 outline-none focus:border-navy transition-colors" />
                <textarea required rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" data-testid="contact-message" className="w-full border border-line px-4 py-3 outline-none focus:border-navy transition-colors resize-none" />
                <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full bg-ink text-[#f2f0ea] py-4 text-[13px] font-bold tracking-[0.18em] hover:bg-navy transition-colors disabled:opacity-60">
                  {loading ? "SENDING..." : "SEND MESSAGE"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
